import type { OrderView } from '@khaddi/contracts';
import { Button, colors, Cup, playChime, Text, unlockAudio, useApi, useRouter, useSession } from '@khaddi/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { View } from 'react-native';

const ACTIVE_STATUSES = new Set(['pending_payment', 'new', 'brewing', 'awaiting_shipper', 'shipper_assigned', 'delivering']);
const POLL_MS = 8_000;
/** An order first seen already finished still rings if it finished this recently (placed and done between polls, or app just reopened). */
const RECENT_MS = 3 * 60_000;
/** The chime repeats until the customer answers, but not forever. */
const REPEAT_CHIME_MS = 15_000;
const MAX_CHIMES = 3;
const VIBRATION = [300, 150, 300, 150, 300];

type Alert = { order: OrderView; kind: 'ready' | 'cancelled' };

const alertKind = (order: OrderView): Alert['kind'] | null => {
  if (order.status === 'completed' && order.channel === 'app_pickup') return 'ready';
  if (order.status === 'cancelled') return 'cancelled';
  return null;
};

/** What changed since the last poll that the customer should hear about. */
const alertsBetween = (before: Map<string, OrderView['status']>, orders: OrderView[], now: number): Alert[] =>
  orders.flatMap((order): Alert[] => {
    const kind = alertKind(order);
    if (!kind) return [];
    const was = before.get(order.id);
    const changed = was ? ACTIVE_STATUSES.has(was) : now - Date.parse(order.updatedAt) < RECENT_MS;
    return changed ? [{ order, kind }] : [];
  });

const RUNG_KEY = 'khaddi.client.rungOrders';

/** Orders already announced on this device, so a reload doesn't ring for them again. */
const rungOrders = (): Set<string> => {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(RUNG_KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
};

const rememberRung = (orderId: string) => {
  try {
    sessionStorage.setItem(RUNG_KEY, JSON.stringify([...rungOrders(), orderId].slice(-20)));
  } catch {
    // storage blocked: worst case a reload rings once more
  }
};

const ring = () => {
  playChime('ready');
  if (typeof navigator !== 'undefined' && 'vibrate' in navigator) navigator.vibrate(VIBRATION);
};

/**
 * Watches the signed-in customer's orders while the app is open and rings when an
 * in-store order is ready to collect (or gets cancelled). No system notifications:
 * it only works while the app is on screen, which is how customers wait at the counter.
 */
export const OrderReadyAlert = () => {
  const api = useApi();
  const { user } = useSession();
  const { navigate } = useRouter();
  const [alert, setAlert] = useState<Alert | null>(null);
  const statuses = useRef<Map<string, OrderView['status']> | null>(null);

  // Browsers keep sound locked until a tap; the customer taps plenty before an order is ready.
  useEffect(() => {
    const onTap = () => unlockAudio();
    window.addEventListener('pointerdown', onTap);
    return () => window.removeEventListener('pointerdown', onTap);
  }, []);

  useEffect(() => {
    statuses.current = null;
    if (!user) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const poll = async () => {
      // a hidden page can't ring anyway; coming back polls at once
      if (document.visibilityState === 'visible') {
        try {
          const orders = await api.orders.mine();
          if (cancelled) return;
          // the very first poll only rings for what finished moments ago, not the whole history
          const rung = rungOrders();
          const next = alertsBetween(statuses.current ?? new Map(), orders, Date.now()).find((a) => !rung.has(a.order.id));
          if (next) {
            rememberRung(next.order.id);
            setAlert(next);
          }
          statuses.current = new Map(orders.map((o) => [o.id, o.status]));
        } catch {
          // offline: try again on the next tick
        }
      }
      if (!cancelled) timer = setTimeout(() => void poll(), POLL_MS);
    };
    // coming back to the app checks at once instead of waiting for the next tick
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      clearTimeout(timer);
      void poll();
    };

    void poll();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [api, user?.id]);

  useEffect(() => {
    if (!alert) return;
    ring();
    let chimes = 1;
    const repeat = setInterval(() => {
      if (++chimes > MAX_CHIMES) return clearInterval(repeat);
      ring();
    }, REPEAT_CHIME_MS);
    const title = document.title;
    document.title = alert.kind === 'ready' ? '🔔 Món đã xong!' : 'Đơn đã hủy';
    return () => {
      clearInterval(repeat);
      document.title = title;
    };
  }, [alert]);

  const dismiss = useCallback(() => setAlert(null), []);
  const open = useCallback(() => {
    if (alert) navigate(`/orders/${encodeURIComponent(alert.order.id)}`);
    setAlert(null);
  }, [alert, navigate]);

  if (!alert) return null;
  const ready = alert.kind === 'ready';
  return (
    <View
      accessibilityRole="alert"
      accessibilityLiveRegion="assertive"
      style={{ position: 'fixed' as unknown as 'absolute', inset: 0, zIndex: 1000, backgroundColor: 'rgba(28, 69, 95, .55)', alignItems: 'center', justifyContent: 'center', padding: 24 }}
    >
      <View style={{ width: '100%', maxWidth: 380, backgroundColor: colors.surface, borderRadius: 28, padding: 24, gap: 12, alignItems: 'center' }}>
        <Cup tint={ready ? colors.sky : colors.border} width={88} />
        <Text variant="display" style={{ textAlign: 'center', fontSize: 28, lineHeight: 32 }}>
          {ready ? 'Món của bạn đã xong!' : 'Đơn đã bị hủy'}
        </Text>
        <Text variant="body" style={{ textAlign: 'center' }}>
          {ready ? `Mời bạn nhận món tại quầy ${alert.order.branch.name}.` : alert.order.cancelReason ? `Lý do: ${alert.order.cancelReason}` : 'Quán đã hủy đơn này.'}
        </Text>
        <Text variant="title" numeric color={colors.primaryText}>
          #{alert.order.code}
        </Text>
        <View style={{ alignSelf: 'stretch', gap: 8, marginTop: 4 }}>
          <Button label={ready ? 'Đã nhận, cảm ơn!' : 'Đóng'} onPress={dismiss} />
          <Button label="Xem đơn" variant="outline" onPress={open} />
        </View>
      </View>
    </View>
  );
};
